import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Menu, Pill } from '../../ui';
import { BookmarkIcon, MoreIcon, RefreshIcon } from '../../ui/icons';
import { useViewActions } from './SavedViewActions';
import { ViewNameDialog } from './SavedViewDialogs';

/**
 * Above the results (phase 3 §5.1): the saved view shown, if any, with its
 * "…" menu (rename, move, delete — the way to them on a phone too); "Update
 * view" once the list moved away from it; "Save this view" when the list is
 * not the whole collection in its default order, or "Save as a new view"
 * beside "Update". `onCreate(name)` and `onUpdate()` resolve once saved.
 * `blocked`: why this list cannot be kept as a view (too long), said beside
 * the disabled buttons.
 */
function SavedViewBar({ inventoryId, active, canSave, summary, onCreate, onUpdate, blocked = null }) {
  const { t } = useTranslation();
  const { items, dialog } = useViewActions(inventoryId);
  const [naming, setNaming] = useState(false);
  const [updating, setUpdating] = useState(false);
  const hintId = useId();
  const blockedProps = blocked ? { disabled: true, 'aria-describedby': hintId } : {};
  const view = active.view;
  const offerSave = view ? active.modified : canSave;
  if (!view && !offerSave) return dialog;

  const update = async () => {
    setUpdating(true);
    try {
      await onUpdate();
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="collection-views" role="group" aria-label={t('views.barLabel')}>
      {view && (
        <span className="collection-views__current">
          <BookmarkIcon size={16} className="collection-views__icon" />
          <span className="visually-hidden">{t('views.current')}</span>
          <span className="collection-views__name">{view.name}</span>
          {active.modified && <Pill tone="amber">{t('views.modified')}</Pill>}
          <Menu
            label={t('views.actionsFor', { name: view.name })}
            items={items(view)}
            trigger={(props) => <IconButton {...props} icon={MoreIcon} size="sm" variant="ghost" label={t('views.actionsFor', { name: view.name })} />}
          />
        </span>
      )}
      {view && active.modified && (
        <Button size="sm" variant="secondary" icon={RefreshIcon} loading={updating} onClick={update} {...blockedProps}>
          {t('views.update')}
        </Button>
      )}
      {offerSave && (
        <Button size="sm" variant={view ? 'ghost' : 'secondary'} icon={BookmarkIcon} onClick={() => setNaming(true)} aria-haspopup="dialog"
                {...blockedProps}>
          {t(view ? 'views.saveAsNew' : 'views.save')}
        </Button>
      )}
      {blocked && (offerSave || active.modified) && <p id={hintId} className="collection-views__hint">{blocked}</p>}
      <ViewNameDialog
        open={naming}
        title={t(view ? 'views.saveAsNew' : 'views.saveTitle')}
        summary={summary}
        submitLabel={t('views.saveButton')}
        onSubmit={async (name) => {
          await onCreate(name);
          setNaming(false);
        }}
        onClose={() => setNaming(false)}
      />
      {dialog}
    </div>
  );
}

export default SavedViewBar;
